// The longer side of a light picture, in pixels, and its JPEG quality
const MAX_SIDE = 800;
const QUALITY = 0.8;

/**
 * A light copy of a picture for saving: at most 800 pixels on its longer side, as a JPEG
 * (usually some tens of kilobytes). A picture that cannot be read is returned as it is.
 */
const shrinkImage = (file: File): Promise<File> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(
        1,
        MAX_SIDE / Math.max(image.naturalWidth, image.naturalHeight)
      );
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
      const context = canvas.getContext('2d');
      if (!context) {
        resolve(file);
        return;
      }

      // A white background under transparent parts, as JPEG has no transparency
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }

          const name = `${file.name.replace(/\.[^.]+$/, '')}.jpg`;
          resolve(new File([blob], name, { type: 'image/jpeg' }));
        },
        'image/jpeg',
        QUALITY
      );
    };

    image.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };

    image.src = url;
  });

export default shrinkImage;
